import { type Dispatch, memo, type SetStateAction } from "react";

import { type ComparativeFeature } from "@doitintl/cmp-models";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";

import { analyticsAlertText } from "../../../assets/texts";
import { reportTxt } from "../../../assets/texts/CloudAnalytics";
import { useReportConfig } from "../Context";
import AnalyticsRenderer from "../renderers/PlotRenderer/AnalyticsRenderer";
import { type PopoverAnchorEl } from "../report/types";
import { type ColKeySort } from "../ReportData";
import { isTable } from "../utilities";
import { CenteredMessage } from "./CenteredMessage";
import CountFieldMissing from "./CountFieldMissing";
import { ErrorMessages } from "./ErrorMessages";
import { useSlowQueryDetection } from "./useSlowQueryDetection";

type Props = {
  isCurrentUserEditor: boolean;
  colKeySort?: ColKeySort | null;
  countFieldMissing: boolean;
  hasUnsavedChanges: boolean;
  forceRender: boolean;
  formatter: (value: number | string | undefined, short?: boolean, comparative?: ComparativeFeature) => string;
  leftDrawerTransitioning: boolean;
  renderKey: number;
  reportError?: { code: string; message?: string };
  reportReady: boolean;
  reportQueryRunning: boolean;
  showForecast: boolean;
  duringCachingProcess: boolean;
  cacheThresholdMs?: number;
  setColKeySort: Dispatch<SetStateAction<ColKeySort | undefined>>;
  setPopoverAnchorEl: (anchorEl: PopoverAnchorEl | null) => void;
  onCacheReportClicked: () => void;
  onCancelReportRunClicked: () => void;
};

const AnalyticsPageContent = ({
  isCurrentUserEditor,
  onCacheReportClicked,
  onCancelReportRunClicked,
  colKeySort,
  countFieldMissing,
  hasUnsavedChanges,
  formatter,
  leftDrawerTransitioning,
  renderKey,
  reportError,
  reportReady,
  reportQueryRunning,
  setColKeySort,
  setPopoverAnchorEl,
  showForecast,
  duringCachingProcess,
  cacheThresholdMs,
}: Props) => {
  const {
    reportConfig: { renderer },
    reportData,
    reportDataWForecasts,
  } = useReportConfig();

  const showSlowQueryMessage = useSlowQueryDetection(reportQueryRunning, cacheThresholdMs) && isCurrentUserEditor;

  const canCacheReport = isCurrentUserEditor || (!isCurrentUserEditor && !hasUnsavedChanges);
  const cacheButtonDisabled = canCacheReport && hasUnsavedChanges;

  const isTableRenderer = isTable(renderer);
  if (leftDrawerTransitioning && !isTableRenderer) {
    return null;
  }

  if (countFieldMissing) {
    return <CountFieldMissing setPopoverAnchorEl={setPopoverAnchorEl} />;
  }

  const handleCacheWhileReportRunning = () => {
    onCancelReportRunClicked();
    onCacheReportClicked();
  };

  if (duringCachingProcess) {
    return (
      <CenteredMessage
        icon={<CircularProgress size={34} />}
        description={
          <>
            {analyticsAlertText.RUNNING_PREVIOUS_1}
            <br />
            {analyticsAlertText.RUNNING_PREVIOUS_2}
          </>
        }
      />
    );
  }

  if (reportQueryRunning) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          pt: 20,
          pb: 5,
        }}
      >
        <CircularProgress size={34} />
        <Typography
          variant="subtitle2"
          sx={{
            mt: 1,
          }}
        >
          {reportTxt.LOADING}
        </Typography>
        {showSlowQueryMessage && canCacheReport && (
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              textAlign: "center",
              maxWidth: 600,
            }}
          >
            This report may take time to run.
            <br />
            {hasUnsavedChanges ? "Please save your report first, then click " : "Click "}
            <strong>Cache report</strong> below to generate it asynchronously.
            <br />
            You will receive a notification once the report is ready.
          </Typography>
        )}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          {showSlowQueryMessage && canCacheReport && (
            <Button variant="contained" onClick={handleCacheWhileReportRunning} disabled={cacheButtonDisabled}>
              {reportTxt.CACHE_REPORT}
            </Button>
          )}
          <Button onClick={onCancelReportRunClicked}>{reportTxt.STOP_GENERATING_REPORT}</Button>
        </Stack>
      </Stack>
    );
  }

  if (reportError) {
    return (
      <ErrorMessages
        onCacheReportClicked={onCacheReportClicked}
        disableCacheBtn={cacheButtonDisabled}
        errorCode={reportError.code}
        errorMessage={reportError?.message}
      />
    );
  } else if (reportReady) {
    const forecastStart = reportData?.current?.getForecastStartIndex() ?? 0;
    const isForecast = showForecast && !!reportDataWForecasts;
    const dataProp = isForecast && reportDataWForecasts.current ? reportDataWForecasts.current : reportData.current;
    if (!dataProp) {
      return null;
    }
    return (
      <AnalyticsRenderer
        colKeySort={colKeySort}
        dataProp={dataProp}
        forecastStart={forecastStart}
        formatter={formatter}
        renderKey={renderKey}
        setColKeySort={setColKeySort}
      />
    );
  }

  return null;
};

export default memo(AnalyticsPageContent);
